/** @jsxRuntime classic */
/** @jsx jsx */
import { useState } from 'react'
import { jsx, css } from '@emotion/react'
import PropTypes from 'prop-types'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import Button from '@material-ui/core/Button'
import { Container } from '@material-ui/core'
import { RoomOutlined, MailOutline, PhoneOutlined } from '@material-ui/icons'
import { withThemeProps } from '@findep/microfronts-core'

import Logo from '../../assets/svg/afi-logo-v3.svg'

import '../../styles/a_href_footer.css'

const footerCss = css`
    background: #1a1a1a;
    color: #ffffff;    
`

const innerCss = css`
    padding: 6px 56px 48px;

    @media (max-width: 959px) {
        padding: 6px 24px 40px;
    }
`

const topSectionCss = css`
    display: grid;
    grid-template-columns: 1.4fr 1fr 1fr;
    gap: 48px;
    padding: 18px 0 16px;

    @media (max-width: 959px) {
        grid-template-columns: 1fr;
        gap: 32px;
    }
`

const brandCss = css`
    min-width: 0;
`

const brandHeaderCss = css`
    display: inline-flex;
    align-items: center;
    gap: 18px;
`

const logoCss = css`
    width: 32px;
    height: 32px;
    object-fit: contain;
`

const brandNameCss = css`
    color: #ffffff;
    font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
    font-size: 20px;
    font-weight: 500;
    line-height: 1.4;
    letter-spacing: 0.05em;
`

const brandTextCss = css`
    margin-top: 18px;
    color: #ffffff;
    font-size: 13.6px;
    line-height: 1.7;
`

const infoListCss = css`
    display: grid;
    gap: 14px;
    margin-top: 22px;
`

const infoItemCss = css`
    display: grid;
    grid-template-columns: 16px 1fr;
    gap: 8px;
    align-items: start;
    color: #ffffff;
    font-size: 13.6px;
    line-height: 1.6;

    &, & > div, & > div * {
        color: #ffffff;
    }

    & > svg {
        color: #ff7a45 !important;
    }

    a {
        color: #ffffff;
        text-decoration: none;
    }
`

const contactLinkCss = css`
    color: #ffffff !important;
    text-decoration: none;

    &:visited,
    &:hover,
    &:active {
        color: #ffffff !important;
    }
`

const columnCss = css`
    display: grid;
    gap: 28px;
`

const sectionCss = css`
    display: grid;
    align-content: start;
    gap: 12px;

    @media (max-width: 959px) {
        gap: 8px;
    }
`

const sectionTitleCss = css`
    color: #ffffff !important;
    font-family: 'Fraunces', 'Georgia', serif;
    font-size: 15.2px;
    font-weight: 600;
    line-height: 1.6;
    letter-spacing: -0.01em;
`

const linkListCss = css`
    display: grid;
    gap: 8px;

    @media (max-width: 959px) {
        gap: 6px;
    }
`

const footerLinkCss = css`
    color: #ffffff !important;
    text-decoration: none;
    font-size: 13.6px;
    line-height: 1.6;

    &:hover {
        color: #ffffff;
        opacity: 0.82;
    }
`

const buttonLinkCss = css`
    padding: 0;
    border: 0;
    background: transparent;
    color: #ffffff !important;
    text-align: left;
    font-size: 13.6px;
    line-height: 1.6;
    cursor: pointer;

    &:hover {
        color: #ffffff;
        opacity: 0.82;
    }
`

const legalLinkResetCss = css`
    &, & a, & p, & span, & strong, & button {
        color: #ffffff !important;
    }
`

const legalSectionCss = css`
    display: grid;
    gap: 20px;
    padding-top: 24px;
    border-top: 1px solid rgba(255, 255, 255, 0.4);
`

const legalTextCss = css`
    color: #ffffff;
    font-size: 12px;
    line-height: 1.7;
    margin: 0;
`

function FooterAfi({
    leyendas,
    terminosyavisosafileft,
    terminosyavisosafiright,
    txtAccesible,
    txtButton,
    txtContent,
    txtDirection,
    txtMapsite,
    section_one,
    section_two,
    tooltip_ccpa,
    btnDepartament,
    brandName,
    brandText,
    customerServiceLabel,
    customerServiceHours,
    companySectionTitle,
    legalSectionTitle,
}) {
    
    const [openDialog, setOpenDialog] = useState(false)
    const [textActive, setTextActive] = useState(false)

    const popAccesible = (opt) => {
        setTextActive(opt === 1)
        setOpenDialog(true)
    }

    const handleClose = () => {
        setOpenDialog(false)
    }

    const siteMapLinks = [...section_one, ...section_two.slice(0, 2)]
    const companyLinks = section_two.slice(2)
    const legalLinks = [
        ...terminosyavisosafileft,
        ...terminosyavisosafiright,
        { texto: txtAccesible, action: () => popAccesible(1) },
        { texto: btnDepartament, action: () => popAccesible(2) },
    ]

    const legalTexts = leyendas
        .map((item, index) => `${item?.onetext || ''} ${item?.twotext || ''}`.trim())
        .filter(Boolean)
        .map((text, index) => ({
            id: index + 1,
            text,
        }))

    return (
        <footer css={footerCss}>
            <Container maxWidth="lg" css={innerCss}>
                <div css={topSectionCss}>
                    <div css={brandCss}>
                        <a href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', textDecoration: 'none' }}>
                            <img src={Logo} alt="Apoyo Financiero logo" css={logoCss} />
                            <div css={brandNameCss}>{brandName}</div>
                        </a>

                        <p css={brandTextCss}>
                            {brandText}
                        </p>

                        <div css={infoListCss}>
                            <div css={infoItemCss}>
                                <RoomOutlined style={{ fontSize: 16 }} />
                                <div>{txtDirection}</div>
                            </div>
                            <div css={infoItemCss}>
                                <MailOutline style={{ fontSize: 16 }} />
                                <a href="mailto:afi@apoyofin.com" css={contactLinkCss}>afi@apoyofin.com</a>
                            </div>
                            <div css={infoItemCss}>
                                <PhoneOutlined style={{ fontSize: 16 }} />
                                <div>
                                    <a href="tel:8008912778" css={contactLinkCss}>800-891-2778</a>
                                    <br />
                                    {customerServiceLabel} · {customerServiceHours}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div css={columnCss}>
                        <section css={sectionCss}>
                            <div css={[sectionTitleCss, legalLinkResetCss]}>{txtMapsite}</div>
                            <div css={legalLinkResetCss}>
                                <div css={linkListCss}>
                                {siteMapLinks.map((item) => (
                                    <a
                                        key={`${item.texto}-${item.link}`}
                                        href={item.link}
                                        target="_blank"
                                        rel="noreferrer"
                                        css={footerLinkCss}
                                    >
                                        {item.texto}
                                    </a>
                                ))}
                                </div>
                            </div>
                        </section>
                    </div>

                    <div css={columnCss}>
                        <section css={sectionCss}>
                            <div css={[sectionTitleCss, legalLinkResetCss]}>{companySectionTitle}</div>
                            <div css={legalLinkResetCss}>
                                <div css={linkListCss}>
                                {companyLinks.map((item) => (
                                    <a
                                        key={`${item.texto}-${item.link}`}
                                        href={item.link}
                                        target="_blank"
                                        rel="noreferrer"
                                        css={footerLinkCss}
                                    >
                                        {item.texto}
                                    </a>
                                ))}
                                </div>
                            </div>
                        </section>

                        <section css={sectionCss}>
                            <div css={[sectionTitleCss, legalLinkResetCss]}>{legalSectionTitle}</div>
                            <div css={legalLinkResetCss}>
                                <div css={linkListCss}>
                                {legalLinks.map((item) =>
                                    item.action ? (
                                        <button key={item.texto} type="button" onClick={item.action} css={buttonLinkCss}>
                                            {item.texto}
                                        </button>
                                    ) : (
                                        <a
                                            key={`${item.texto}-${item.link}`}
                                            href={item.link}
                                            target="_blank"
                                            rel="noreferrer"
                                            css={footerLinkCss}
                                        >
                                            {item.texto}
                                        </a>
                                    )
                                )}
                                </div>
                            </div>
                        </section>
                    </div>
                </div>

                <div css={legalSectionCss}>
                    {legalTexts.map((item) => (
                        <p key={item.id} css={legalTextCss}>
                            {item.text}
                        </p>
                    ))}
                </div>
            </Container>

            <Dialog open={openDialog} onClose={handleClose} id="dialogo">
                <DialogContent>{textActive ? txtContent : tooltip_ccpa}</DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary" autoFocus>
                        {txtButton}
                    </Button>
                </DialogActions>
            </Dialog>
        </footer>
    )
}

FooterAfi.propTypes = {
    linkone: PropTypes.array,
    linktwo: PropTypes.array,
    linktree: PropTypes.array,
    leyendas: PropTypes.array,
    imagestext: PropTypes.array,
    condusefarray: PropTypes.array,
    buroarray: PropTypes.array,
    otros: PropTypes.array,
    fontsectionone: PropTypes.object,
    fontsectiontwo: PropTypes.object,
    fontsectiontree: PropTypes.object,
    terminosyavisos: PropTypes.array,
    colorfooter: PropTypes.string,
    colorhr: PropTypes.string,
    afi: PropTypes.bool,
    terminosyavisosafileft: PropTypes.array,
    terminosyavisosafiright: PropTypes.array,
}

FooterAfi.defaultProps = {
    brandName: 'Apoyo Financiero',
    brandText: 'Comprometidos con la comunidad latina en Estados Unidos. Soluciones financieras accesibles, transparentes y con corazón.',
    customerServiceLabel: 'Atención al cliente',
    customerServiceHours: 'Lun - Sáb | 8am - 8pm',
    companySectionTitle: 'Empresa',
    legalSectionTitle: 'Legal',
    txtDirection: (
        <span>
            <strong>Apoyo Financiero, Inc.</strong>
            <br />
            2300 Clayton Rd. Ste 1170
            <br />
            Concord, CA 94520
        </span>
    ),
    txtMapsite: 'Mapa de Sitio',
    section_one: [
        { link: '/es/prestamos-personales', texto: 'PRÉSTAMOS' },
        { link: '/es/como-funciona', texto: '¿CÓMO FUNCIONA?' },
        { link: '/es/medios-de-pago', texto: 'OPCIONES DE PAGO' },
        { link: '/es/sucursales', texto: 'SUCURSALES' },
        { link: '/es/dudas', texto: '¿TIENES DUDAS?' },
        { link: '/es/testimonios', texto: 'TESTIMONIOS' },
    ],
    section_two: [
        { link: '/es/clientes', texto: 'CLIENTES' },
        { link: '/es/educacion-financiera', texto: 'EDUCACIÓN FINANCIERA' },
        { link: '/es/nuestra-empresa', texto: 'ACERCA DE APOYO FINANCIERO' },
        { link: '/es/inversionistas', texto: 'INVERSIONISTAS' },
        { link: '/es/trabaja-con-nosotros', texto: 'TRABAJA CON NOSOTROS' },
        { link: '/es/contacto', texto: 'CONTÁCTANOS' },
    ],
    section_subend:
        'Estamos constituidos como una SOFOM, ENR, es decir, no requerimos autorización de la SHCP para la realización de operaciones de crédito, y estamos sujetos a la supervisión de la Comisión Nacional Bancaria y de Valores únicamente para efectos de lo dispuesto por el artículo 56 de la Ley General de Organizaciones y Actividades Auxiliares del Crédito',
    section_end: [' © 2026 Todos los derechos reservados.', 'Apoyo Financiero, Inc. 2300 Clayton Road,', ' Suite 1170 Concord, California 94520.'],
    btnDepartament: 'Igualdad de Oportunidades para todos',
    tooltip_ccpa:
        'De acuerdo a lo establecido por las leyes federales y las políticas del Departamento del Tesoro esta organización no puede discriminar por causa de raza, color, origen nacional, sexo, edad, o porque una persona tiene impedimentos.   Para presentar una queja sobre discriminación, escriba a: U.S. Department of the Treasury, Director, Office of Civil Rights and Equal Employment Opportunity  1500 Pennsylvania Avenue, N.W., Washington, DC 20220; o envíe un correo electrónico a: crcomplaints@treasury.gov ',
}

export default withThemeProps(FooterAfi, 'VDNPFooterAfi')
